import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getRequest } from "../../Apis/apiWrapper";
import { qaEndpoints } from "../../utils/constants/httpConstants";
import { nativeCaptureSupported, captureViewport, MAX_SHOTS } from "../../utils/qa/qaCapture";
import ReportModal from "./ReportModal";
import { styles } from "./qaStyles";
import { GREY, BASE, BRAND, BORDER } from "../../utils/constants/colors";

const EMPTY_DRAFT = { kind: "bug", description: "", shots: [] };
const CLOSE_ANIMATION_MS = 250;
const INK_RAIL = GREY.QUATINARY; // #5A5C60 - matches SidebarItem default label color

// Inline so the button needs no icon package; inherits the label colour.
const BugIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m8 2 1.88 1.88" />
    <path d="M14.12 3.88 16 2" />
    <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1" />
    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6" />
    <path d="M12 20v-9" />
    <path d="M6.53 9C4.6 8.8 3 7.1 3 5" />
    <path d="M6 13H2" />
    <path d="M3 21c0-2.1 1.7-3.9 3.8-4" />
    <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4" />
    <path d="M22 13h-4" />
    <path d="M17.2 17c2.1.1 3.8 1.9 3.8 4" />
  </svg>
);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Root of the Live QA reporter. The Report click captures NOTHING - the dialog opens
// instantly (un-freezable by construction). During a native grab BOTH the dialog and
// the floating button are hidden so neither photobombs the shot, and the dialog
// always reopens with the draft intact whether the grab succeeded or was dismissed.
const QaReporter = ({ sidebar = false }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [capturing, setCapturing] = useState(false);
  const [stashMode, setStashMode] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [aiEnabled, setAiEnabled] = useState(false);
  const probedRef = useRef(false);

  const supported = nativeCaptureSupported();

  const refreshBadge = useCallback(async () => {
    try {
      const res = await getRequest(navigate, qaEndpoints.pending);
      setPendingCount(res?.data?.data?.count || 0);
    } catch (err) {
      // unauthenticated or transient - badge just stays as-is
    }
  }, [navigate]);

  useEffect(() => {
    refreshBadge();
  }, [refreshBadge]);

  // Probe AI availability once, lazily on first open (no key -> buttons hide).
  useEffect(() => {
    if (!open || probedRef.current) return;
    probedRef.current = true;
    (async () => {
      try {
        const res = await getRequest(navigate, qaEndpoints.assist);
        setAiEnabled(res?.data?.data?.enabled === true);
      } catch (err) {
        setAiEnabled(false);
      }
    })();
  }, [open, navigate]);

  const appendShot = useCallback((shot) => {
    setDraft((prev) => ({
      ...prev,
      shots: prev.shots.length < MAX_SHOTS ? [...prev.shots, shot] : prev.shots,
    }));
  }, []);

  // Hide dialog + button, wait out the close animation, grab one frame, then ALWAYS
  // come back - cancel/denial/timeout simply mean "no shot", never an error state.
  const runCapture = useCallback(async () => {
    setOpen(false);
    setCapturing(true);
    await sleep(CLOSE_ANIMATION_MS);
    const shot = await captureViewport();
    if (shot) appendShot(shot);
    setCapturing(false);
    setStashMode(false);
    setOpen(true);
  }, [appendShot]);

  const handleStash = useCallback(() => {
    setOpen(false);
    setStashMode(true);
  }, []);

  const handleButtonClick = () => {
    if (stashMode) {
      runCapture();
    } else {
      setOpen(true);
    }
  };

  if (capturing) return null;

  // The normal Report trigger ALWAYS lives in one fixed spot - the bottom of the left
  // sidebar rail (or floating bottom-left when not mounted in a sidebar). It never
  // switches position based on what else is open, so it can't jump around or overlap
  // panels. Its z-index sits above modal backdrops so it stays clickable even when a
  // full-screen modal (e.g. Create Campaign) is open.
  const normalTrigger = !open && !stashMode && (
    sidebar ? (
      <button
        type="button"
        data-qa-exclude="true"
        style={styles.railButton}
        onClick={handleButtonClick}
        title="Report a bug or request a feature"
      >
        <span style={{ color: INK_RAIL, display: "flex" }}>
          <BugIcon />
        </span>
        <span style={{ ...styles.railLabel, color: INK_RAIL }}>Report</span>
        {pendingCount > 0 && <span style={styles.railBadge}>{pendingCount}</span>}
      </button>
    ) : (
      <button
        type="button"
        data-qa-exclude="true"
        style={styles.fab}
        onClick={handleButtonClick}
        title="Report a bug or request a feature"
      >
        <BugIcon />
        Report
        {pendingCount > 0 && <span style={styles.fabBadge}>{pendingCount}</span>}
      </button>
    )
  );

  return (
    <>
      {normalTrigger}
      {/* Stash mode is an EXPLICIT "navigate away and capture" flow the reporter opted
          into - here a floating capture trigger + a back-to-draft escape are expected
          and temporary. This is the only time a floating button appears. */}
      {stashMode && !open && (
        <>
          <button
            type="button"
            data-qa-exclude="true"
            style={{ ...styles.fab, background: BRAND.SECONDARY, color: BASE.WHITE, border: "none" }}
            onClick={handleButtonClick}
            title="Capture this screen for your report"
          >
            Capture this screen
          </button>
          <button
            type="button"
            data-qa-exclude="true"
            style={{ ...styles.fab, right: "200px", background: BASE.WHITE, color: GREY.QUATINARY, border: `1px solid ${BORDER.DEFAULT}` }}
            onClick={() => { setStashMode(false); setOpen(true); }}
          >
            Back to report
          </button>
        </>
      )}
      <ReportModal
        open={open}
        onClose={() => { setOpen(false); setStashMode(false); refreshBadge(); }}
        draft={draft}
        setDraft={setDraft}
        nativeSupported={supported}
        onNativeCapture={runCapture}
        onStash={handleStash}
        aiEnabled={aiEnabled}
        refreshBadge={refreshBadge}
      />
    </>
  );
};

export default QaReporter;
